/*
 * Copyright (c) Logan Group Services - Gaston Longhitano <gaston.longhitano@logangroupservices.com>
 * All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, {useEffect, useRef} from 'react';

import './Controls.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlay, faPause , faBug, faCompress, faExpand, faCog, faVolumeUp, faVolumeMute} from '@fortawesome/free-solid-svg-icons'
import RangeSlider from 'react-rangeslider'
import 'react-rangeslider/lib/index.css'
import ReactTooltip from 'react-tooltip';
import {useMediaQuery} from "react-responsive";
import {useRecoilValue} from 'recoil';
import {currentGameInfoQuery} from '../recoil/selectors';
import {
  currentFullscreenModeState,
  currentPlayingTimeState,
  currentVideoResolutionState, displayControlsState,
  durationState,
  muteState,
  playingState,
  playtimeState,
} from '../recoil/atoms';
import {useRecoilState} from 'recoil/dist';
import {calculateBitrateBandwidth, BITRATE_AUTO} from '../Helper';

function Controls({videoRef, handleFullscreen}) {
  const currentGame  = useRecoilValue(currentGameInfoQuery);
  const isFullscreen = useRecoilValue(currentFullscreenModeState);
  const showControls = useRecoilValue(displayControlsState);

  const [currentVideoResolution, setCurrentVideoResolution] = useRecoilState(currentVideoResolutionState);
  const [isPlaying, setIsPlaying] = useRecoilState(playingState);
  const [playTime, setPlayTime] = useRecoilState(playtimeState);
  const [isVolMute, setIsMute] = useRecoilState(muteState);
  const [currentTime, setCurrentTime] = useRecoilState(currentPlayingTimeState);
  const [duration, setDuration] = useRecoilState(durationState);



  const isMobile = useMediaQuery({ maxWidth: 575 });

  const tooltip = useRef();

  useEffect(() => {
    const time = formatTime(
        Math.round(videoRef.current.duration)
    );
    setDuration(`${time.minutes}:${time.seconds}`);

    videoRef.current.addEventListener('timeupdate', updateTimeElapsed);

    // If resolution is auto, listen and adapt to connection speed change
    if (navigator.connection) {
      navigator.connection.onchange = () => {
        if (currentVideoResolution === BITRATE_AUTO)
          setCurrentVideoResolution(calculateBitrateBandwidth());
      }
    }
  }, []);

  const updateTimeElapsed = () => {
    if(videoRef.current) {
      if (videoRef.current.currentTime) {
        const time = formatTime(Math.round(videoRef.current.currentTime));
        setCurrentTime(`${time.minutes}:${time.seconds}`);
        setPlayTime(videoRef.current.currentTime);
      }

      if (videoRef.current.duration) {
        const duration = formatTime(
            Math.round(videoRef.current.duration)
        );
        setDuration(`${duration.minutes}:${duration.seconds}`);
      }

      if (!videoRef.current.paused) {
        setIsPlaying(true);
      }
    }
  };

  const handleTogglePlay = () => {
    if (videoRef.current.paused || videoRef.current.ended) {
      videoRef.current.play();
      setIsPlaying(true);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleToggleMute = () => {
    videoRef.current.muted = !isVolMute;
    setIsMute(!isVolMute);
  };

  // // PLAYBAR FUNCTIONS
  const handlePlayChangeStart = (e) => {
    videoRef.current.pause();
  };

  const handlePlayTimeChange = playTime => {
    setPlayTime(playTime);
  };

  const handlePlayChangeComplete = (value) => {
     videoRef.current.currentTime = playTime;
     videoRef.current.play();
   };

  const handleResolutionChange = (resolution) => {
    tooltip.current.tooltipRef = null;
    ReactTooltip.hide();
    setCurrentVideoResolution(resolution);
  };

  return (
      <div className={`player-controls d-flex align-items-center ${showControls ? '' : 'mouse--still'}`}>
        <div className="player-controls__left">
          <a className="player-controls__link --play" data-playing={isPlaying} onClick={handleTogglePlay}>
            { (isPlaying &&  <FontAwesomeIcon icon={faPause} />) ||  <FontAwesomeIcon icon={faPlay} />}
          </a>
        </div>
        <div className="player-controls__playbar">
          <RangeSlider
              min={0}
              max={videoRef.current.duration}
              value={playTime}
              onChangeStart={handlePlayChangeStart}
              onChange={handlePlayTimeChange}
              onChangeComplete={handlePlayChangeComplete}
              tooltip={false}
          />
        </div>
        <div className="player-controls__right">
          {
            !isMobile &&
            <div className="time__container">
              <span className="time__playtime">{currentTime}</span>
              <span className="time__remaining">{duration}</span>
            </div>
          }
          <div className="volume__container">
            <a className="player-controls__link --vol" onClick={handleToggleMute}>
              {(isVolMute && <FontAwesomeIcon icon={faVolumeMute} />) || <FontAwesomeIcon icon={faVolumeUp} />}
            </a>
          </div>
          <a className="player-controls__link --settings" data-for="settings" data-event="click" data-tip=''>
            <FontAwesomeIcon icon={faCog} />
          </a>
          <ReactTooltip id="settings" effect="solid" type="dark" clickable={true} globalEventOff="click" ref={tooltip}>
            { [ BITRATE_AUTO, ...currentGame.resolutions ].map((resolution) => <div className={`clickable ${currentVideoResolution === resolution? 'yellow':''}`} key={resolution} data-tip='' onClick={() => {
              handleResolutionChange(resolution)
            }}>{resolution}</div>)}
          </ReactTooltip>

          <a className="player-controls__link --bug" onClick={e => e.preventDefault()} data-tip="report a bug" data-for="bug">
            <FontAwesomeIcon icon={faBug} />
          </a>
          <ReactTooltip id="bug" effect="solid" type="dark" />
          {
            !isMobile &&
            <a className="player-controls__link --fullscreen"
               onClick={handleFullscreen}>
              { isFullscreen === true ? <FontAwesomeIcon icon={faCompress} /> : <FontAwesomeIcon icon={faExpand} /> }
            </a>
          }
        </div>
      </div>
  )
}

function formatTime(timeInSeconds) {
  const result = new Date(timeInSeconds * 1000).toISOString().substr(11, 8);

  return {
    minutes: result.substr(3, 2),
    seconds: result.substr(6, 2),
  };
}





export default Controls;
